import { Game } from '../gql-types';
import {
    MGameGameConfiguration,
    MGameStepConfiguration,
} from '../configuration/games/m-game/mGameContentConfiguration';
import {
    ZdarGameGameConfiguration,
    ZdarGameStepConfiguration,
} from '../configuration/games/zdar-escape-game/zdarEscapeGameContentConfiguration';
import {
    ZdarKidsGameGameConfiguration,
    ZdarKidsGameStepConfiguration,
} from '../configuration/games/zdar-kids-game/zdarKidsGameContentConfiguration';
import { genericHookContextBuilder } from '../util/genericHookContextBuilder';
import { getGameConfiguration } from '../configuration/gamesConfiguration';
import { useCallback, useEffect, useState } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';

const DEFAULT_CURRENT_STEP = 1;
const DEFAULT_SHOWN_HINTS = 0;
const DEFAULT_CONFIGURATION: ZdarGameGameConfiguration | MGameGameConfiguration | ZdarKidsGameGameConfiguration | null =
    null;
const DEFAULT_STEPS = 36;

function useGame() {
    const [configuration, setConfiguration] = useLocalStorage('sifrovka-configuration', DEFAULT_CONFIGURATION);
    const [lastStep, setLastStep] = useLocalStorage('sifrovka-configuration-steps', DEFAULT_STEPS);
    const [currentStep, setCurrentStep] = useState(DEFAULT_CURRENT_STEP);
    const [shownHints, setShownHints] = useState(DEFAULT_SHOWN_HINTS);
    const [stepContent, setStepContent] = useState<
        ZdarGameStepConfiguration | MGameStepConfiguration | ZdarKidsGameStepConfiguration
    >({ id: 0 });

    const setGameConfiguration = useCallback((configurationName, lastStep) => {
        const newConfiguration = getGameConfiguration(configurationName);
        setConfiguration(newConfiguration);
        setCurrentStep(DEFAULT_CURRENT_STEP);
        setShownHints(DEFAULT_SHOWN_HINTS);
        setLastStep(lastStep);
    }, []);

    const incrementStep = useCallback(() => {
        if (configuration && configuration[currentStep + 1]) {
            setCurrentStep(currentStep + 1);
        }
        setShownHints(DEFAULT_SHOWN_HINTS);
    }, [configuration, currentStep]);

    const selectHint = useCallback(() => {
        setShownHints(shownHints + 1);
    }, [shownHints]);

    type Props = {
        availableGame?: Game | null;
        startGame?: (token: string | null) => void;
        endGame?: (token: string | null) => void;
    };
    const nextStepAction = ({ availableGame, startGame, endGame }: Props) => {
        if (currentStep === 1 && availableGame && availableGame.started === null && startGame) {
            startGame(availableGame.token || '');
        } else if (currentStep === lastStep && availableGame && availableGame.ended === null && endGame) {
            endGame(availableGame.token || '');
        }
        if (currentStep < lastStep) {
            incrementStep();
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentStep]);

    const state = {
        configuration,
        currentStep,
        shownHints,
        stepContent,
        lastStep,
    };
    return {
        ...state,
        setGameConfiguration,
        incrementStep,
        selectHint,
        nextStepAction,
        setCurrentStep,
        setStepContent,
    };
}

export const { ContextProvider: GameContextProvider, Context: GameContext } = genericHookContextBuilder(useGame);
